import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { addClientContact } from "@/lib/api";
import { formatPhoneNumber } from "@/lib/phoneFormat";

// "+ Add new contact" modal for a Work Order's Site Contact dropdown.
// Saves directly to the selected site via the existing
// /clients/:id/contacts?site_id=... endpoint and bubbles the saved
// contact back to the parent so the dropdown updates without a refresh.
// Extracted from WorkOrderTab.jsx (Feb 2026, iter174).
export function WOAddContactDialog({
  open, onOpenChange,
  clientId, siteId,
  onContactAdded,
}) {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", role: "",
  });

  const reset = () => setForm({ name: "", phone: "", email: "", role: "" });

  const handleSave = async () => {
    const name = (form.name || "").trim();
    if (!name) {
      toast.error("Contact name is required");
      return;
    }
    try {
      // Always save as non-primary so the existing primary stays put.
      const payload = { ...form, is_primary: false };
      const res = await addClientContact(clientId, payload, siteId);
      const saved = res?.data || { id: `tmp-${Date.now()}`, ...payload };
      onContactAdded(saved);
      toast.success(`Added ${saved.name || name}`);
      onOpenChange(false);
      reset();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add contact");
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Site Contact</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-medium">Name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Full name"
              data-testid="input-new-contact-name"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs font-medium">Phone</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: formatPhoneNumber(e.target.value) }))}
                placeholder="303-867-5309"
                maxLength={20}
                data-testid="input-new-contact-phone"
              />
            </div>
            <div>
              <Label className="text-xs font-medium">Role</Label>
              <Input
                value={form.role}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                placeholder="Facility mgr"
                data-testid="input-new-contact-role"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium">Email</Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="contact@example.com"
              data-testid="input-new-contact-email"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={handleCancel}>Cancel</Button>
            <Button onClick={handleSave} data-testid="btn-save-new-contact">
              Save Contact
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
